import { PasswordRule, StrengthConfig } from "./types";

const mergeRules = (
  defaultRules: PasswordRule[],
  customRules: PasswordRule[],
): PasswordRule[] => {
  const customRuleIds = customRules.map((rule) => rule.id);
  const filteredDefaults = defaultRules.filter(
    (rule) => !customRuleIds.includes(rule.id),
  );
  return [...filteredDefaults, ...customRules];
};

const evaluatePasswordRules = (password: string, rules: PasswordRule[]) => {
  return rules.map((rule) => ({
    ...rule,
    passed: rule.test(password),
  }));
};

const calculateStrengthLevel = (
  password: string,
  ruleResults: Array<PasswordRule & { passed: boolean }>,
): number => {
  if (password.length === 0) return 0;

  const visibleRules = ruleResults.filter((rule) => !rule.hidden);

  if (visibleRules.length === 0) return 1;

  const passedCount = visibleRules.filter((rule) => rule.passed).length;
  const passedRatio = passedCount / visibleRules.length;

  if (passedRatio === 1) return 4;
  if (passedRatio >= 0.8) return 3;
  if (passedRatio >= 0.6) return 2;

  return 1;
};

const getStrengthConfigByLevel = (
  password: string,
  strengthLevel: number,
  strengthConfig: StrengthConfig,
): { label: string; textColor: string } => {
  const defaultColor = "text-ashShade-3";

  if (password.length === 0) {
    return {
      label: "",
      textColor: defaultColor,
    };
  }

  return {
    label: strengthConfig[strengthLevel]?.label || "Unknown",
    textColor: strengthConfig[strengthLevel]?.textColor || defaultColor,
  };
};

const getSegmentColor = (
  index: number,
  strengthLevel: number,
  strengthSegments: number,
  password: string,
  strengthConfig: StrengthConfig,
): string => {
  const activeSegments = Math.ceil((strengthLevel / 4) * strengthSegments);
  const defaultColor = "bg-ashShade-3";

  if (password.length === 0) return defaultColor;
  if (index < activeSegments) {
    return strengthConfig[strengthLevel]?.bgColor || defaultColor;
  }
  return defaultColor;
};

export {
  mergeRules,
  evaluatePasswordRules,
  calculateStrengthLevel,
  getStrengthConfigByLevel,
  getSegmentColor,
};
